import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import SiteHeader from '@/components/SiteHeader'
import SiteFooter from '@/components/SiteFooter'
import Seo from '@/components/Seo'
import { useScrollReveal } from '@/hooks/useScrollReveal'
import { signUpUrl } from '@/lib/auth'
import './landing.css'
import './security.css'

const PILLARS = [
  {
    title: 'Encrypted, end to end',
    body:
      'Everything you tell Pelvia is encrypted in transit (TLS 1.2+) and at rest (AES-256). Your symptom logs, intake answers, and clinical notes are never stored in plain text on our systems.',
    icon: (
      <>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </>
    ),
  },
  {
    title: 'De-identified by design',
    body:
      'Your clinical data and your identity live apart. When our models reason over your case, they work on de-identified records keyed to a token, not your name, email, or contact details.',
    icon: (
      <>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <line x1="19" y1="8" x2="19" y2="14" />
        <line x1="22" y1="11" x2="16" y2="11" />
      </>
    ),
  },
  {
    title: 'Least-privilege access',
    body:
      'Access is scoped and logged. The only person who sees your full clinical picture is the board-certified physician reviewing your case. No marketing team, no data broker, no curious engineer.',
    icon: (
      <>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="m9 12 2 2 4-4" />
      </>
    ),
  },
  {
    title: 'Never sold. Ever.',
    body:
      'We do not sell your personal data, and we never will. Our business is getting you care faster, not monetizing your health. That promise is written into our Privacy Policy, not buried in it.',
    icon: (
      <>
        <path d="M3 3l18 18" />
        <path d="M21 12c0 4.97-4.03 9-9 9a8.96 8.96 0 0 1-5.5-1.87" />
        <path d="M3.5 16.5A8.96 8.96 0 0 1 3 12c0-4.97 4.03-9 9-9 2.05 0 3.94.69 5.45 1.84" />
      </>
    ),
  },
]

const PIPELINE = [
  {
    step: '01',
    title: 'You tell us, in your own words',
    body:
      'Our intake was purpose-built for pelvic pain: hormonal cycles, flare triggers, symptom patterns, the detail that gets lost in a ten-minute appointment. Your raw answers are encrypted the moment they leave your device.',
  },
  {
    step: '02',
    title: 'A grounded clinical model structures it',
    body:
      'Pelvia’s intelligence runs on retrieval-augmented generation: every clinical statement is grounded in vetted sources (ACOG practice bulletins, peer-reviewed literature) rather than a model’s open-ended guess. We track the source behind each conclusion so nothing is invented out of thin air.',
  },
  {
    step: '03',
    title: 'A board-certified physician reviews and signs off',
    body:
      'No diagnosis is delivered by a machine alone. A licensed OB/GYN reviews the structured note, corrects it, and signs off. The model accelerates the physician. It never replaces them.',
  },
  {
    step: '04',
    title: 'You decide what happens next',
    body:
      'You own the output: a complete clinical note, validated symptom scores, and a referral to an excision surgeon if you choose. You control what is shared, with whom, and when.',
  },
]

const COMMITMENTS = [
  'We minimize what we collect. If we don’t need it to care for you, we don’t ask for it.',
  'We separate who you are from what you tell us wherever we technically can.',
  'We isolate health data from analytics and marketing systems.',
  'We log access to clinical records and review those logs.',
  'We let you export or delete your data. It’s yours, not ours.',
  'We will tell you plainly if anything ever changes.',
]

export default function SecurityPage() {
  useScrollReveal()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="landing security-page">
      <Seo
        title="Security & Privacy | Pelvia"
        description="How Pelvia protects your health data: end-to-end encryption, de-identification, least-privilege access, grounded clinical AI with physician sign-off, and a promise we never sell your data."
        path="/security"
        image="/static/og/security.png"
      />
      <SiteHeader />

      <header className="sec-hero">
        <div className="sec-hero-inner">
          <div className="section-label reveal">Security &amp; privacy</div>
          <h1 className="sec-hero-title reveal reveal-delay-1">
            Your data is yours. We <em>engineered</em> it that way.
          </h1>
          <p className="sec-hero-sub reveal reveal-delay-2">
            Pelvia handles some of the most personal information a person has. We treat the software
            that holds it with the same rigor as the medicine it supports, and we wrote this page so
            you can see exactly how, without a law degree.
          </p>
          <div className="sec-hero-meta reveal reveal-delay-3">
            <span>End-to-end encrypted</span>
            <span className="sec-dot">&middot;</span>
            <span>De-identified by design</span>
            <span className="sec-dot">&middot;</span>
            <span>Never sold</span>
          </div>
        </div>
      </header>

      <section className="sec-pillars">
        <div className="sec-container">
          <div className="sec-pillar-grid">
            {PILLARS.map((p) => (
              <div className="sec-pillar reveal" key={p.title}>
                <div className="sec-pillar-icon" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    {p.icon}
                  </svg>
                </div>
                <h3>{p.title}</h3>
                <p>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="sec-pipeline">
        <div className="sec-container">
          <div className="sec-section-head reveal">
            <div className="section-label">How the technology works</div>
            <h2 className="sec-section-title">
              Powerful AI, kept on a <em>leash.</em>
            </h2>
            <p className="sec-section-desc">
              The reason to trust Pelvia isn’t that we use AI. It’s how we constrain it. Every step is
              grounded in evidence and gated by a human clinician.
            </p>
          </div>

          <ol className="sec-pipeline-list">
            {PIPELINE.map((s) => (
              <li className="sec-pipeline-step reveal" key={s.step}>
                <span className="sec-pipeline-num">{s.step}</span>
                <div>
                  <h3>{s.title}</h3>
                  <p>{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="sec-commitments">
        <div className="sec-container">
          <div className="sec-commitments-card reveal">
            <div className="section-label">Our commitments</div>
            <h2 className="sec-section-title">Plain-English promises.</h2>
            <ul className="sec-commitments-list">
              {COMMITMENTS.map((c) => (
                <li key={c}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  {c}
                </li>
              ))}
            </ul>
            <p className="sec-commitments-foot">
              The full detail lives in our <Link to="/privacy">Privacy Policy</Link> and{' '}
              <Link to="/terms">Terms</Link>. Questions? Email{' '}
              <a href="mailto:support@pelvia.me">support@pelvia.me</a>.
            </p>
          </div>
        </div>
      </section>

      <section className="sec-cta">
        <div className="sec-cta-inner">
          <h2 className="reveal">Care you can trust, with privacy you can verify.</h2>
          <p className="reveal reveal-delay-1">
            From symptom to surgeon referral in 72 hours, built on infrastructure designed to keep
            your most personal data exactly that: yours.
          </p>
          <a href={signUpUrl()} className="btn-primary reveal reveal-delay-2">
            Start your intake
            <svg className="btn-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </a>
        </div>
      </section>

      <SiteFooter />
    </div>
  )
}
